/**
 * Gallery-view field getters for DocTypeListPageShell (title / subtitle / image per row).
 *
 * Extracted from DocTypeListPageShell (file-size split, behaviour unchanged).
 */

import { useCallback, useMemo } from "react";
import type { ListSettingsFieldConfig } from "@/lib/hooks/listSettingsTypes";
import type { DocTypeListPageShellProps } from "./DocTypeListPageShell.types";

export function useShellGalleryFields<T>({
  galleryConfig,
  availableFields,
  detectedImageFieldnames,
  thumbnailLabelFieldnames,
}: {
  galleryConfig: DocTypeListPageShellProps<T>["galleryConfig"];
  availableFields: ListSettingsFieldConfig[] | undefined;
  detectedImageFieldnames: string[];
  thumbnailLabelFieldnames: string[];
}) {
  // Gallery field getters (reuse existing image/label field detection)
  const getGalleryImagePath = useCallback((row: T): string | null => {
    const raw = row as unknown as Record<string, unknown>;
    for (const f of detectedImageFieldnames) {
      const v = raw[f];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return null;
  }, [detectedImageFieldnames]);

  const galleryTitleFieldnames = useMemo(() => {
    const explicit = galleryConfig?.titleFieldnames;
    if (explicit?.length) {
      const available = new Set(
        (availableFields ?? []).map((f) => f.fieldname),
      );
      if (available.size > 0) {
        const filtered = explicit.filter((f) => f === 'name' || available.has(f));
        if (filtered.length > 0) return filtered;
      }
      return explicit;
    }
    return thumbnailLabelFieldnames.length > 0 ? thumbnailLabelFieldnames : ['name'];
  }, [galleryConfig?.titleFieldnames, thumbnailLabelFieldnames, availableFields]);

  const getGalleryTitleText = useCallback((row: T): string => {
    const raw = row as unknown as Record<string, unknown>;
    for (const f of galleryTitleFieldnames) {
      const v = raw[f];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return String(raw.name ?? '');
  }, [galleryTitleFieldnames]);

  const gallerySubtitleFieldnames = useMemo(() => {
    const explicit = galleryConfig?.subtitleFieldnames;
    if (explicit?.length) {
      const available = new Set(
        (availableFields ?? []).map((f) => f.fieldname),
      );
      if (available.size > 0) {
        return explicit.filter((f) => available.has(f));
      }
    }
    return explicit ?? [];
  }, [galleryConfig?.subtitleFieldnames, availableFields]);

  const getGallerySubtitleText = useCallback((row: T): string | undefined => {
    if (gallerySubtitleFieldnames.length === 0) return undefined;
    const raw = row as unknown as Record<string, unknown>;
    for (const f of gallerySubtitleFieldnames) {
      const v = raw[f];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return undefined;
  }, [gallerySubtitleFieldnames]);

  return {
    getGalleryImagePath,
    galleryTitleFieldnames,
    getGalleryTitleText,
    gallerySubtitleFieldnames,
    getGallerySubtitleText,
  };
}
